import { useQueryParams } from '@terminal/hooks/use-query-params/useQueryParams';
import {
  DRAWER_PARAM_KEYS,
  getCleanDrawerParams,
  queryKeys,
  queryValues,
} from '@terminal/routes/query-params';
import { PosthogTrackingSource } from '@terminal/services/tracking/posthog/constants';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { useEffect, useMemo } from 'react';

export enum SubTabsType {
  Preview = 'preview',
  Recap = 'recap',
}

const trackedDrawers = new Set<string>();

export const useEarningsInsightDrawer = () => {
  const { deleteQueryParam, setQueryParams, params } = useQueryParams();
  const { trackEarningsInsightDrawerOpen } = usePosthogEvents();
  const isDrawerOpen =
    params[queryKeys.activeDrawer] === queryValues.earningsInsightDrawer;
  const activeEntityTag = params[queryKeys.activeEarningsEntity];
  const earningsId = params[queryKeys.earningsId];

  const drawerId = useMemo(() => {
    return activeEntityTag && earningsId
      ? `${activeEntityTag}-${earningsId}`
      : null;
  }, [activeEntityTag, earningsId]);

  const subtabs = [SubTabsType.Preview, SubTabsType.Recap];
  const foundTabIndex = subtabs.findIndex(
    tab => tab === params[queryKeys.activeDrawerTab]
  );
  const activeTabIndex = foundTabIndex !== -1 ? foundTabIndex : 0;
  const activeTab = subtabs[activeTabIndex];

  const openDrawer = ({
    entityTag,
    earningsId,
    subtab,
    source,
  }: {
    entityTag: string;
    earningsId: string;
    subtab?: SubTabsType;
    source: PosthogTrackingSource;
  }) => {
    const drawerType = subtab ?? SubTabsType.Preview;
    trackEarningsInsightDrawerOpen(source, drawerType);
    setQueryParams({
      ...getCleanDrawerParams(),
      [queryKeys.activeDrawer]: queryValues.earningsInsightDrawer,
      [queryKeys.activeDrawerTab]: drawerType,
      [queryKeys.activeEarningsEntity]: entityTag,
      [queryKeys.earningsId]: earningsId,
    });
  };

  const closeDrawer = () => {
    deleteQueryParam([...DRAWER_PARAM_KEYS, queryKeys.drawerExpanded]);
  };

  const switchTab = (index: number) => {
    setQueryParams({ [queryKeys.activeDrawerTab]: subtabs[index] });
  };

  const utmSource = params[queryKeys.utmSource];

  useEffect(() => {
    if (!isDrawerOpen || !drawerId || !utmSource) {
      if (!isDrawerOpen && drawerId && trackedDrawers.has(drawerId)) {
        trackedDrawers.delete(drawerId);
      }

      return;
    }

    if (trackedDrawers.has(drawerId)) {
      return;
    }

    trackedDrawers.add(drawerId);

    trackEarningsInsightDrawerOpen(
      utmSource as PosthogTrackingSource,
      activeTab
    );
  }, [isDrawerOpen, drawerId]);

  return {
    closeDrawer,
    openDrawer,
    isDrawerOpen,
    activeEntityTag,
    switchTab,
    activeTab,
    earningsId,
  };
};
